import { useState, useEffect } from 'react';
import { MapPin, Navigation, Loader2, X, Send, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function LocationPicker({ onSend, onClose }) {
  const [mode, setMode] = useState('current'); // current | search
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const getCurrentLocation = () => {
    setLoading(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      setLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Reverse geocode
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const data = await res.json();
          setLocation({
            lat: latitude,
            lng: longitude,
            name: data.display_name?.split(',').slice(0, 3).join(',') || 'My Location',
            address: data.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          });
        } catch {
          setLocation({
            lat: latitude,
            lng: longitude,
            name: 'My Location',
            address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          });
        }
        setLoading(false);
      },
      (err) => {
        toast.error('Location access denied');
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`);
      const data = await res.json();
      setSearchResults(data.map(r => ({
        lat: parseFloat(r.lat),
        lng: parseFloat(r.lon),
        name: r.display_name?.split(',').slice(0, 2).join(',') || r.display_name,
        address: r.display_name,
      })));
    } catch {
      toast.error('Search failed');
    }
    setSearching(false);
  };

  const selectResult = (result) => {
    setLocation(result);
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleSend = () => {
    if (!location) return;
    onSend(location);
  };

  const mapUrl = location
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${location.lng - 0.005},${location.lat - 0.003},${location.lng + 0.005},${location.lat + 0.003}&layer=mapnik&marker=${location.lat},${location.lng}`
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50" data-testid="location-picker">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <h3 className="font-bold text-slate-800">Share Location</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid="location-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-100">
          <button
            onClick={() => setMode('current')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mode === 'current' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500'}`}
            data-testid="loc-tab-current"
          >
            <Navigation className="w-4 h-4 inline mr-1.5" />Current
          </button>
          <button
            onClick={() => setMode('search')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mode === 'search' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500'}`}
            data-testid="loc-tab-search"
          >
            <Search className="w-4 h-4 inline mr-1.5" />Search Place
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {mode === 'current' ? (
            <>
              {!location ? (
                <button
                  onClick={getCurrentLocation}
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-60"
                  data-testid="get-location-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Navigation className="w-5 h-5" />}
                  {loading ? 'Getting location...' : 'Share Current Location'}
                </button>
              ) : (
                <div className="space-y-3">
                  {/* Map Preview */}
                  <div className="rounded-xl overflow-hidden border border-stone-200 h-44">
                    <iframe
                      src={mapUrl}
                      className="w-full h-full border-0"
                      title="Location"
                    />
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                    <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm">{location.name}</p>
                      <p className="text-xs text-slate-500 truncate">{location.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Search Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search for a place..."
                  className="flex-1 px-4 py-2.5 bg-stone-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-300"
                  data-testid="location-search-input"
                />
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="px-4 py-2.5 bg-orange-500 text-white rounded-xl font-medium text-sm hover:bg-orange-600 disabled:opacity-50"
                  data-testid="location-search-btn"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-1">
                  {searchResults.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => selectResult(r)}
                      className="w-full flex items-start gap-3 p-3 hover:bg-orange-50 rounded-xl text-left transition-colors"
                      data-testid={`location-result-${i}`}
                    >
                      <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 text-sm">{r.name}</p>
                        <p className="text-xs text-slate-500 line-clamp-2">{r.address}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected location preview */}
              {location && (
                <div className="space-y-3">
                  <div className="rounded-xl overflow-hidden border border-stone-200 h-44">
                    <iframe
                      src={mapUrl}
                      className="w-full h-full border-0"
                      title="Location"
                    />
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                    <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm">{location.name}</p>
                      <p className="text-xs text-slate-500 truncate">{location.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Send Button */}
        {location && (
          <div className="px-4 py-3 border-t border-stone-100">
            <button
              onClick={handleSend}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors shadow-md"
              data-testid="send-location-btn"
            >
              <Send className="w-4 h-4" /> Send Location
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
